import { Component, lazy, Suspense, type ReactNode } from "react";
import { PhoneOff } from "lucide-react";

import { useAccount, type CallingFlags } from "@/providers/AccountProvider";

// Where AppShell mounts calling -- the dock once, outside <main> and outside the routed page's
// boundary, and the phone launcher in the icon rail (docs/vault/Plans/WhatsApp-Calling.md §11).
//
// Both are their own chunks, so a workspace without calling never downloads the softphone. Each
// gets its OWN Suspense boundary on purpose: AppShell has none, and the nearest one is App.tsx's,
// around every route, whose fallback is the whole-app skeleton. Without these, a chunk suspending
// on an ordinary update -- the gate turning true after identity loads -- would swap the entire
// app, page included, for that skeleton. And each its own error boundary: a crash costs only
// that piece, never the page, the rail or the other piece.

const CallDock = lazy(() => import("@/calls/CallDock"));
const CallLauncher = lazy(() => import("@/calls/CallLauncher"));

const CALLS_FAILED = "Calls stopped working. Reload the page to take calls again.";

/** §10: only where calling is live, to a real seat that may answer. The server checks again. */
export function dockAllowed(calling: CallingFlags, can: (permission: string) => boolean): boolean {
  return calling.platform && calling.enabled && calling.seat && can("calls.answer");
}

interface CallsErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface CallsErrorBoundaryState {
  failed: boolean;
}

class CallsErrorBoundary extends Component<CallsErrorBoundaryProps, CallsErrorBoundaryState> {
  override state: CallsErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): CallsErrorBoundaryState {
    return { failed: true };
  }

  override render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

const DOCK_FAILED = (
  <p
    role="alert"
    className="fixed bottom-4 right-4 z-40 max-w-[calc(100vw-2rem)] rounded-[var(--radius-ctl)] border-hairline border-brand-border bg-brand-surface px-3 py-2 text-[12px] text-n-fg-muted"
  >
    {CALLS_FAILED}
  </p>
);

const LAUNCHER_FAILED = (
  <span
    role="img"
    aria-label={CALLS_FAILED}
    title={CALLS_FAILED}
    className="flex size-10 items-center justify-center text-n-critical"
  >
    <PhoneOff size={18} aria-hidden="true" />
  </span>
);

export function CallDockRoot() {
  const { calling, can } = useAccount();
  if (!dockAllowed(calling, can)) return null;
  return (
    <CallsErrorBoundary fallback={DOCK_FAILED}>
      <Suspense fallback={null}>
        <CallDock />
      </Suspense>
    </CallsErrorBoundary>
  );
}

/** The phone launcher, for the foot of the icon rail. Same gate as the dock. */
export function CallLauncherSlot() {
  const { calling, can } = useAccount();
  if (!dockAllowed(calling, can)) return null;
  return (
    <div className="flex justify-center pb-3">
      <CallsErrorBoundary fallback={LAUNCHER_FAILED}>
        {/* The button's own size while its chunk loads, so the rail does not shift. */}
        <Suspense fallback={<span className="size-10" aria-hidden="true" />}>
          <CallLauncher />
        </Suspense>
      </CallsErrorBoundary>
    </div>
  );
}
